import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { AuthProvider } from '@/contexts/AuthContext';
import { OrgConfigProvider } from '@/contexts/OrgConfigContext';
import { ToastProvider } from '@/components/ui/Toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProtectedRoute } from '@/components/guards/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { NotFound } from '@/components/NotFound';
import { Skeleton } from '@/components/ui/Skeleton';

// Auth pages (not lazy — small + needed immediately)
import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage';
import { ChangePasswordPage } from '@/pages/auth/ChangePasswordPage';

// Lazy-loaded feature pages
const HomePage = lazy(() => import('@/pages/home/HomePage').then((m) => ({ default: m.HomePage })));
const GearListPage = lazy(() => import('@/pages/gear/GearListPage').then((m) => ({ default: m.GearListPage })));
const GearDetailPage = lazy(() => import('@/pages/gear/GearDetailPage').then((m) => ({ default: m.GearDetailPage })));
const PeoplePage = lazy(() => import('@/pages/people/PeoplePage').then((m) => ({ default: m.PeoplePage })));
const PersonDetailPage = lazy(() => import('@/pages/people/PersonDetailPage').then((m) => ({ default: m.PersonDetailPage })));
const ImportPage = lazy(() => import('@/pages/import/ImportPage').then((m) => ({ default: m.ImportPage })));
const InspectionsPage = lazy(() => import('@/pages/inspections/InspectionsPage').then((m) => ({ default: m.InspectionsPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const ActivityPage = lazy(() => import('@/pages/activity/ActivityPage').then((m) => ({ default: m.ActivityPage })));
const AccountPage = lazy(() => import('@/pages/account/AccountPage').then((m) => ({ default: m.AccountPage })));

function PageSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton variant="line" />
      <Skeleton variant="card" />
      <Skeleton variant="card" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <Routes>
                {/* Public auth routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />

                {/* Forced change password (auth required but outside shell) */}
                <Route
                  path="/change-password"
                  element={
                    <ProtectedRoute>
                      <ChangePasswordPage />
                    </ProtectedRoute>
                  }
                />

                {/* Authenticated routes with layout shell */}
                <Route
                  element={
                    <ProtectedRoute>
                      <OrgConfigProvider>
                        <AppShell />
                      </OrgConfigProvider>
                    </ProtectedRoute>
                  }
                >
                  <Route
                    index
                    element={
                      <Suspense fallback={<PageSkeleton />}>
                        <HomePage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="gear"
                    element={
                      <Suspense fallback={<PageSkeleton />}>
                        <GearListPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="gear/:id"
                    element={
                      <Suspense fallback={<PageSkeleton />}>
                        <GearDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="people"
                    element={
                      <ProtectedRoute roles={['admin', 'quartermaster']}>
                        <Suspense fallback={<PageSkeleton />}>
                          <PeoplePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/:id"
                    element={
                      <ProtectedRoute roles={['admin', 'quartermaster']}>
                        <Suspense fallback={<PageSkeleton />}>
                          <PersonDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="import"
                    element={
                      <ProtectedRoute roles={['admin', 'quartermaster']}>
                        <Suspense fallback={<PageSkeleton />}>
                          <ImportPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="inspections"
                    element={
                      <Suspense fallback={<PageSkeleton />}>
                        <InspectionsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <ProtectedRoute roles={['admin', 'quartermaster']}>
                        <Suspense fallback={<PageSkeleton />}>
                          <SettingsPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="activity"
                    element={
                      <ProtectedRoute roles={['admin', 'quartermaster']}>
                        <Suspense fallback={<PageSkeleton />}>
                          <ActivityPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="account"
                    element={
                      <Suspense fallback={<PageSkeleton />}>
                        <AccountPage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
